import { selectUserInfo } from "features/user/userSlice";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  InputAdornment,
  // Step,
  // StepLabel,
  // Stepper,
  TextField,
  useMediaQuery,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ClientProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
import { ReactComponent as Client } from "assets/SideBarIcons/customer.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import Loader from "layout/Loader";
import DynamicSelect from "common/Select/Select";
import ClientTabs from "./ClientTabs";
import CommonCheckbox from "common/CheckBox/Checkbox";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  ClientRequest,
  GetClientByIdResponse,
} from "subModule/src/services/client/interface";
import BasicDatePicker from "common/Pickers/Pickers";
import { useGetPriceListDropdownService } from "subModule/src/services/pricelist/usePriceList";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormDialog from "common/Modal/Modal";
import { useGetHostingDetailsService } from "subModule/src/services/client/useClient";
interface FormData {
  [key: string]: any;
}
const steps = ["Details", "contractManagement"];
export default function ClientsModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "customers",
    moduleLabelKey: "customersList",
    moduleHref: "/clients",
    entityFallbackLabelKey: "name",
    isEdit: !!params?.id,
  });
  // True only when listing passed account.contractClient (via navigate state / sessionStorage)
  const isContractClientFromList = (() => {
    const fromState = (location.state as { contractClient?: boolean } | null)
      ?.contractClient;
    if (typeof fromState === "boolean") return fromState;
    if (params?.id) {
      return (
        sessionStorage.getItem(`client-contractClient-${params.id}`) === "true"
      );
    }
    return false;
  })();

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [client, setClient] = useState<any>({});
  const [updateModal, setUpdateModal] = useState<boolean>(false);
  const loggedInUserData = useSelector(selectUserInfo);
  const canManageContractClient =
    loggedInUserData?.type === -1 || loggedInUserData?.type === 100;
  const hasContractClientPrivilege = params?.id
    ? checkPrivileges("ROLE_CONTRACT_CLIENT_UPDATE")
    : checkPrivileges("ROLE_CONTRACT_CLIENT_CREATE");
  const showContractManagement =
    canManageContractClient && hasContractClientPrivilege;
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, dirtyFields, isDirty },
    watch,
    setValue,
    reset,
    trigger,
    getValues,
  } = useForm<FormData>({
    defaultValues: {
      account: {
        invoicingPreference: { value: 1, label: t("Individual") },
      },
    },
  });
  const { data: priceListData, refetch: priceListRefetch } =
    useGetPriceListDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { data: hostingDetailsData, refetch: hostingRefetch } =
    useGetHostingDetailsService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const priceLists = (priceListData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const hostingList = (hostingDetailsData?.pages[0]?.data?.content || []).map(
    (item: any) => ({ value: item.id, label: item.title }),
  );
  // const loggedInUserType = loggedInUserData.type;
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedClientCompanyPlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phClientCompanyAr") : t("phClientCompanyEn");
  const localizedContactPersonPlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phContactPersonAr") : t("phContactPersonEn");
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id ? false : true,
  );
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [username, setUsername] = useState<string>("");
  const isScreenMediumOrGreater = useMediaQuery("(min-width:900px)");
  const companyTypes = [
    { value: 1000, label: t("soleProprietor") },
    { value: 1100, label: t("Corporate") },
    { value: 1200, label: t("Government") },
  ];
  const invoicingPreferences = [
    { value: 1, label: t("Individual") },
    { value: 2, label: t("consolidated") },
  ];
  const ownerClientTypeOptions: ReadonlyArray<{
    label: string;
    value: number;
  }> = [{ label: "Bayanat", value: 1 }];
  const sourceList = [
    { label: t("Walk-in"), value: 1 },
    { label: t("Phone / WhatsApp"), value: 2 },
    { label: t("Online"), value: 3 },
    { label: t("Referral"), value: 4 },
    { label: t("Insurance"), value: 5 },
    { label: t("Corporate / Fleet"), value: 6 },
    { label: t("Returning Customer"), value: 7 },
    { label: t("OTHER"), value: 8 },
  ];
  useEffect(() => {
    priceListRefetch();
    if (params?.id) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    if (currentStep === 1 && canManageContractClient) {
      hostingRefetch();
    }
  }, [currentStep, canManageContractClient]);
  useEffect(() => {
    setCurrentStep(0);
  }, []);
  useEffect(() => {
    if (!showContractManagement && currentStep !== 0) {
      setCurrentStep(0);
    }
  }, [showContractManagement, currentStep]);
  useEffect(() => {
    if (
      currentStep === 0 &&
      showContractManagement &&
      watch("account.contractClient")
    ) {
      setIsBtnEnabled(true);
    }
  }, [currentStep]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const isContractClient = watch("account.contractClient");
  useEffect(() => {
    if (loggedInUserData?.type === -1 && !isContractClient) {
      setValue("accountClientType", null);
    }
  }, [isContractClient, loggedInUserData?.type, setValue]);
  const getByID = () => {
    const clientTypeName =
      canManageContractClient && isContractClientFromList
        ? "clients/contractclient"
        : "clients";
    ClientProfileService.getClientByID(+(params as any)?.id, clientTypeName)
      .then((res: GetClientByIdResponse) => {
        if (res.success) {
          const accountClientTypeRaw =
            res?.data?.accountClientType ??
            res?.data?.account?.accountClientType ??
            res?.data?.account?.clientType;
          reset({
            ...(res?.data || {}),
            username:
              res?.data?.autoDetails === 2 ||
              res?.data?.autoDetails === 10 ||
              res?.data?.autoDetails === 11 ||
              res?.data?.autoDetails === 12 ||
              res?.data?.autoDetails === 20 ||
              res?.data?.autoDetails === 21
                ? ""
                : res?.data?.username,
            account: (() => {
              const accountFromApi = { ...(res?.data?.account || {}) };
              delete (accountFromApi as { clientType?: unknown }).clientType;
              return {
                ...accountFromApi,
                contractClient:
                  isContractClientFromList ||
                  Boolean(accountFromApi?.contractClient),
                title: res.data.account?.title ? res.data.account?.title : "",
                companyType: res.data.account?.companyType
                  ? {
                      value: res.data.account?.companyType || "",
                      label:
                        companyTypes[
                          companyTypes.findIndex(
                            (v) => v.value === res.data.account?.companyType,
                          )
                        ]?.label || "",
                    }
                  : null,
                priceListId: res.data.account?.priceListId
                  ? {
                      value: res.data.account?.priceListId,
                      label:
                        res?.data?.account?.priceListTitle ||
                        res.data.account?.priceListId,
                    }
                  : null,
                invoicingPreference: res.data.account?.invoicingPreference
                  ? {
                      value: res.data.account?.invoicingPreference || "",
                      label:
                        invoicingPreferences[
                          invoicingPreferences.findIndex(
                            (v) =>
                              v.value === res.data.account?.invoicingPreference,
                          )
                        ]?.label || "",
                    }
                  : { value: 1, label: t("Individual") },
                hostingId: res.data.account?.hostingId
                  ? {
                      value: res.data.account?.hostingId,
                      label:
                        res?.data?.account?.hostingTitle ||
                        res.data.account?.hostingId,
                    }
                  : null,
              };
            })(),
            addressesRequest:
              res?.data?.addressResponse || res?.data?.account?.address
                ? {
                    ...res?.data?.addressResponse,
                    address:
                      res?.data?.addressResponse?.address ||
                      res?.data?.account?.address ||
                      "",
                  }
                : null,
            source:
              res?.data?.source && res?.data?.source !== "0"
                ? {
                    value: Number(res?.data?.source) || "",
                    label:
                      sourceList.find(
                        (v) => v.value === Number(res?.data?.source),
                      )?.label || res?.data?.source,
                  }
                : null,
            accountClientType:
              accountClientTypeRaw != null
                ? {
                    value: Number(accountClientTypeRaw),
                    label:
                      ownerClientTypeOptions.find(
                        (o) => o.value === Number(accountClientTypeRaw),
                      )?.label ?? "Bayanat",
                  }
                : null,
          });
          setUsername(res?.data?.username || "");
          captureLabel(res?.data?.name || res?.data?.account?.title);
          if (
            currentStep === 0 &&
            showContractManagement &&
            (isContractClientFromList || res?.data?.account?.contractClient)
          ) {
            setIsBtnEnabled(true);
          }
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const ownerClientTypeFormToPayload = (
    selection: unknown,
  ): { accountClientType: number } | Record<string, never> => {
    if (loggedInUserData?.type !== -1) return {};
    if (
      selection == null ||
      typeof selection !== "object" ||
      !("value" in selection)
    )
      return {};
    const v = (selection as { value: unknown }).value;
    if (v == null) return {};
    const n = Number(v);
    return Number.isNaN(n) ? {} : { accountClientType: n };
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    if (params?.id) {
      delete data?.account?.userId;
      delete data?.addressResponse;
      const accountClientTypeSelection = data.accountClientType;
      const updateFormData = { ...data };
      delete (
        updateFormData as {
          clientType?: unknown;
          accountClientType?: unknown;
        }
      ).clientType;
      delete (updateFormData as { accountClientType?: unknown })
        .accountClientType;
      const accountForUpdate = { ...(data.account || {}) };
      delete (accountForUpdate as { clientType?: unknown }).clientType;
      const existingLoginEmail = data?.username || username || null;
      const updatePayload: any = {
        ...updateFormData,
        type: 160,
        email: existingLoginEmail,
        username: existingLoginEmail,
        loginAccess: Boolean(data?.loginAccess),
        account: {
          ...accountForUpdate,
          companyType: data?.account?.companyType?.value
            ? data.account?.companyType.value
            : 0,
          priceListId: data.account?.priceListId.value,
          invoicingPreference: data?.account?.invoicingPreference?.value
            ? data.account?.invoicingPreference.value
            : null,
          hostingId: data.account?.hostingId?.value || null,
          ...ownerClientTypeFormToPayload(accountClientTypeSelection),
        },
        addressesRequest: {
          ...data?.addressesRequest,
          id: data?.addressesRequest?.id || null,
        },
        source: data?.source?.value || data?.source || null,
        ...ownerClientTypeFormToPayload(accountClientTypeSelection),
      };
      if (!data?.loginAccess) {
        updatePayload.email = null;
        updatePayload.username = null;
        delete updatePayload.password;
        delete updatePayload.confirmPassword;
        delete updatePayload.showPassword;
        delete updatePayload.showConfirmPassword;
      }
      setClient(updatePayload);
      setUpdateModal(true);
    } else {
      const accountClientTypeSelection = data.accountClientType;
      const createFormData = { ...data };
      delete (
        createFormData as {
          clientType?: unknown;
          accountClientType?: unknown;
        }
      ).clientType;
      delete (createFormData as { accountClientType?: unknown })
        .accountClientType;
      delete createFormData.showConfirmPassword;
      delete createFormData.showPassword;
      delete createFormData.confirmPassword;
      const accountForCreate = { ...(data.account || {}) };
      delete (accountForCreate as { clientType?: unknown }).clientType;
      ClientProfileService.createClient(
        {
          ...createFormData,
          type: 160,
          email: data?.loginAccess ? data?.username : null,
          username: data?.loginAccess ? data?.username : null,
          loginAccess: Boolean(data?.loginAccess),
          active: true,
          business: {},
          account: {
            ...accountForCreate,
            companyType: data?.account?.companyType?.value
              ? data.account?.companyType.value
              : 0,
            priceListId: data.account?.priceListId.value,
            invoicingPreference: data?.account?.invoicingPreference?.value
              ? data.account?.invoicingPreference.value
              : null,
            hostingId: data.account?.hostingId?.value || null,
            ...ownerClientTypeFormToPayload(accountClientTypeSelection),
          },
          source: data?.source?.value || data?.source || null,
          ...ownerClientTypeFormToPayload(accountClientTypeSelection),
        },
        canManageContractClient &&
          checkPrivileges("ROLE_CONTRACT_CLIENT_CREATE") &&
          watch("account.contractClient")
          ? "clients/contractclient"
          : "clients",
      )
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("client") + " " + t("created") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate("/clients");
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };
  const updateClient = (payload: ClientRequest) => {
    ClientProfileService.updateClient(
      Number(params?.id),
      payload,
      canManageContractClient &&
        checkPrivileges("ROLE_CONTRACT_CLIENT_UPDATE") &&
        (isContractClientFromList || watch("account.contractClient"))
        ? "clients/contractclient"
        : "clients",
    )
      .then((response: any) => {
        if (response.success) {
          captureLabel(payload?.name || payload?.account?.title);
          navigate(
            `/client/${params?.id}/account/${params?.accountId}/invoicingPreference/${response?.data?.account?.invoicingPreference}`,
          );
          enqueueSnackbar(
            <AlertMessages
              statusCode={response.status}
              text={t("updated") + " " + t("successfully")}
            />,
            { variant: "success" },
          );
        } else {
          enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      })
      .finally(() => setIsBtnEnabled(true));
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "priceLists")
        priceListRefetch().finally(() => setSearchLoading(null));
      else if (
        typeName === "hostingDetails" &&
        currentStep === 1 &&
        canManageContractClient
      )
        hostingRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <>
      <ComponentHeaderTwo
        breadcrumbItems={breadcrumbItems}
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/clients")}
        onSubmit={handleSubmit(onSubmit)}
        heading={
          (params?.id ? t("editCustomer") : t("addCustomer")) +
          " (" +
          t("company") +
          ")"
        }
        icon={<Client height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={checkPrivileges("ROLE_CLIENT_CREATE")}
      />
      <div className={`${params?.id ? "flex" : ""}`}>
        {params?.id ? <ClientTabs params={params} selectedIndex={0} /> : null}
        {loading ? (
          <Loader />
        ) : (
          <SimpleModal
            visable={true}
            padding={false}
            title={
              (params?.id ? t("editCustomer") : t("addCustomer")) +
              " (" +
              t("company") +
              ")"
            }
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            content={
              <Grid
                container
                component="form"
                id="form"
                className="mb-2 px-2"
                spacing={2}
                onSubmit={handleSubmit(onSubmit)}
                sx={{
                  "& .MuiStep-horizontal .Mui-active + .MuiStepLabel-labelContainer":
                    {
                      background: `linear-gradient(20.78deg, ${themeColors?.secondary}, ${themeColors?.secondary + "B3"})`,
                    },
                }}
              >
                {params?.id &&
                (watch("totalDueAmount") || watch("totalDueAmount") === 0) ? (
                  <Grid item xs={12}>
                    <Typography
                      className="font-semibold font-Saira text-[14px] flex gap-1 justify-end"
                      sx={{ color: themeColors?.primary }}
                    >
                      <span
                        className="font-medium italic"
                        style={{ color: themeColors?.secondary }}
                      >
                        {t("totalDueAmount")}
                      </span>
                      <span style={{ color: themeColors?.secondary }}>:</span>
                      <span className="font-bold">
                        {new Intl.NumberFormat("en", {
                          notation: "standard",
                          maximumFractionDigits: 2,
                        }).format(watch("totalDueAmount") || "")}
                      </span>
                      <span className="font-[400] uppercase">
                        {loggedInUserData?.business?.currency || ""}
                      </span>
                    </Typography>
                  </Grid>
                ) : null}
                {showContractManagement ? (
                  <>
                    {/* <Stepper
                      className="mt-[5px] ms-[15px] reportSteps w-[100%]"
                      activeStep={currentStep}
                    >
                      {steps.map((label, index) => {
                        const stepProps: { completed?: boolean } = {};
                        const labelProps: { optional?: React.ReactNode } = {};
                        return (
                          <Step key={index} {...stepProps}>
                            <StepLabel
                              {...labelProps}
                              className={`${index === 1 && !watch("account.contractClient") ? "opacity-25" : "opacity-100"}`}
                            >
                              {t(label)}
                            </StepLabel>
                          </Step>
                        );
                      })}
                    </Stepper> */}
                    {currentStep === 0 ? (
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="account.contractClient"
                          control={control}
                          defaultValue={false}
                          render={({ field }) => (
                            <CommonCheckbox
                              alignWithFieldLabel={false as any}
                              {...field}
                              label={t("contractClient")}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => field.onChange(event.target.checked)}
                              checked={field.value}
                              color={themeColors?.primary}
                            />
                          )}
                        />
                      </Grid>
                    ) : null}
                  </>
                ) : null}
                <Grid item container xs={12} spacing={2}>
                  {currentStep === 0 ? (
                    <>
                      <Grid item xs={12}>
                        <h1
                          className="flex font-Saira font-[700] text-[17px]"
                          style={{ color: themeColors?.primary }}
                        >
                          {t("companyDetails")}
                        </h1>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("account.title", {
                            validate: {
                              required: (value) =>
                                value?.trim() !== "" ||
                                t("This field is required."),
                            },
                          })}
                          label={`${t("company")} ${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${primaryLanguage})` : ""} *`}
                          placeholder={localizedClientCompanyPlaceholder(
                            primaryLanguage,
                          )}
                          title={`${t("company")} ${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${primaryLanguage})` : ""} *`
                            .replace(/\s+/g, " ")
                            .trim()}
                          fullWidth
                          error={!!(errors.account as any)?.title}
                          helperText={
                            (errors.account as any)?.title?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("account.secondaryTitle")}
                          label={`${t("company")} ${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${secondaryLanguage})` : ""}`}
                          placeholder={localizedClientCompanyPlaceholder(
                            secondaryLanguage,
                          )}
                          title={`${t("company")} ${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${secondaryLanguage})` : ""}`
                            .replace(/\s+/g, " ")
                            .trim()}
                          fullWidth
                          error={!!(errors.account as any)?.secondaryTitle}
                          helperText={
                            (errors.account as any)?.secondaryTitle
                              ?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("account.taxNumber", {
                            validate: {
                              required: (value) =>
                                value?.trim() !== "" ||
                                t("This field is required."),
                            },
                          })}
                          label={t("VAT Number") + " *"}
                          placeholder={t("phClientVatNumber")}
                          title={t("VAT Number") + " *"}
                          fullWidth
                          error={!!(errors.account as any)?.taxNumber}
                          helperText={
                            (errors.account as any)?.taxNumber
                              ?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          type="email"
                          {...register("account.email", {
                            required: t("This field is required."),
                            pattern: {
                              value:
                                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                              message: t("validEmail"),
                            },
                          })}
                          label={t("email") + " *"}
                          placeholder={t("phClientEmail")}
                          title={t("email")}
                          fullWidth
                          error={!!(errors.account as any)?.email}
                          helperText={
                            (errors.account as any)?.email?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          type="text"
                          {...register("account.phone", {
                            validate: {
                              phoneLength: (value) =>
                                value === "" ||
                                value === null ||
                                (value?.length >= 5 && value?.length <= 17) ||
                                t("phoneLength"),
                            },
                          })}
                          label={t("phoneNumber")}
                          placeholder={t("phCompanyPhone")}
                          title={t("phoneNumber")}
                          fullWidth
                          error={!!(errors.account as any)?.phone}
                          helperText={
                            (errors.account as any)?.phone?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                          onChange={(
                            e: ChangeEvent<
                              HTMLInputElement | HTMLTextAreaElement
                            >,
                          ) => {
                            const cleaned = e.target.value.replace(
                              /[^0-9+]/g,
                              "",
                            );
                            setValue("account.phone", cleaned);
                            if (!IsBtnEnabled) setIsBtnEnabled(true);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          type="number"
                          {...register("account.creditLimit")}
                          onChange={(e: any) => {
                            const value = e.target.value;
                            e.target.value = value < 0 ? 0 : value || "";
                            if (!IsBtnEnabled) setIsBtnEnabled(true);
                          }}
                          label={t("creditLimit")}
                          placeholder={t("phCreditLimit")}
                          title={t("creditLimit")}
                          fullWidth
                          error={!!(errors.account as any)?.creditLimit}
                          helperText={
                            (errors.account as any)?.creditLimit
                              ?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          type="number"
                          {...register("account.creditPeriod")}
                          onChange={(e: any) => {
                            const value = e.target.value;
                            e.target.value = value < 0 ? 0 : value || "";
                            if (!IsBtnEnabled) setIsBtnEnabled(true);
                          }}
                          label={t("creditPeriod")}
                          placeholder={t("phCreditPeriod")}
                          title={t("creditPeriod")}
                          fullWidth
                          error={!!(errors.account as any)?.creditPeriod}
                          helperText={
                            (errors.account as any)?.creditPeriod
                              ?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="account.companyType"
                          control={control}
                          defaultValue={null}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("companyType")}
                              defaultPlaceholder="phSelectCompanyType"
                              {...field}
                              options={companyTypes}
                              isSearchable
                              isClearable
                              onChange={(selectedOption: any) =>
                                field.onChange(selectedOption)
                              }
                              error={
                                (errors.account as any)?.companyType
                                  ?.message as string
                              }
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="account.priceListId"
                          control={control}
                          defaultValue={null}
                          rules={{ required: t("This field is required.") }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("priceList") + " *"}
                              defaultPlaceholder="phSelectApplicablePriceList"
                              {...field}
                              options={priceLists}
                              isSearchable
                              onChange={(selectedOption: any) =>
                                field.onChange(selectedOption)
                              }
                              error={
                                (errors.account as any)?.priceListId
                                  ?.message as string
                              }
                              isLoading={searchLoading === "priceLists"}
                              onInputChange={(
                                search: string,
                                { action }: { action: string },
                              ) => {
                                if (
                                  action === "input-change" ||
                                  (action !== "set-value" &&
                                    action !== "menu-close" &&
                                    action !== "input-blur")
                                )
                                  handleDropdownActions(
                                    "priceLists",
                                    "searchIt",
                                    search,
                                  );
                                else if (
                                  action === "input-blur" ||
                                  action === "menu-close"
                                ) {
                                  if (dropdownSearchTerm || pageNo > 1)
                                    handleDropdownActions(
                                      "priceLists",
                                      "searchIt",
                                      "",
                                    );
                                  else setDropdownSearchTerm("");
                                  setPageNo(1);
                                }
                              }}
                              onMenuScrollToBottom={() => {
                                if (
                                  !(priceListData?.pages[0]?.data as any).last
                                )
                                  handleDropdownActions(
                                    "priceLists",
                                    "loadNext",
                                  );
                              }}
                              page={pageNo}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="account.invoicingPreference"
                          control={control}
                          defaultValue={null}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("invoicingPreference")}
                              defaultPlaceholder="phSelectInvoicingPreference"
                              {...field}
                              options={invoicingPreferences}
                              isSearchable
                              onChange={(selectedOption: any) =>
                                field.onChange(selectedOption)
                              }
                              error={
                                (errors.account as any)?.invoicingPreference
                                  ?.message as string
                              }
                            />
                          )}
                        />
                      </Grid>
                      {loggedInUserData?.type === -1 && isContractClient ? (
                        <Grid item xs={12} md={3}>
                          <Controller
                            name="accountClientType"
                            control={control}
                            rules={{ required: false }}
                            render={({ field }) => (
                              <DynamicSelect
                                label={t("clientType")}
                                defaultPlaceholder="phSelectClientType"
                                {...field}
                                options={[...ownerClientTypeOptions]}
                                isSearchable={false}
                                isClearable
                                onChange={(
                                  selectedOption: {
                                    label: string;
                                    value: number;
                                  } | null,
                                ) => field.onChange(selectedOption)}
                                error={
                                  errors?.accountClientType?.message as string
                                }
                              />
                            )}
                          />
                        </Grid>
                      ) : null}
                      {/* <Grid item xs={12} md={3}>
                                        <Controller
                                            name="account.customerApprovalRequired"
                                            control={control}
                                            defaultValue={false}
                                            render={({ field }) => (<div>
                                                <CommonCheckbox
                                                    {...field}
                                                    label={t("customerApproval")}
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => field.onChange(event.target.checked)}
                                                    checked={field.value}
                                                    color={themeColors?.primary}
                                                />
                                            </div>)}
                                        />
                                    </Grid> */}
                    </>
                  ) : null}
                  {showContractManagement && currentStep === 1 ? (
                    <>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("account.contractNumber", {
                            validate: {
                              required: (value) =>
                                !watch("account.contractClient") ||
                                value?.trim() !== "" ||
                                t("This field is required."),
                            },
                          })}
                          label={
                            t("contractNumber") +
                            (watch("account.contractClient") ? " *" : "")
                          }
                          placeholder={t("phContractNumber")}
                          title={
                            t("contractNumber") +
                            (watch("account.contractClient") ? " *" : "")
                          }
                          fullWidth
                          error={!!(errors.account as any)?.contractNumber}
                          helperText={
                            (errors.account as any)?.contractNumber
                              ?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="account.contractStart"
                          control={control}
                          rules={{
                            validate: (value) => {
                              const date = new Date(value);
                              return isNaN(date.getTime()) && value
                                ? t("Invalid Date")
                                : true;
                            },
                          }}
                          render={({ field }) => (
                            <BasicDatePicker
                              type="datePicker"
                              {...field}
                              label={t("contractStartDate")}
                              fullWidth
                              onChange={(value: any) => {
                                field.onChange(
                                  new Date(value)
                                    .toLocaleDateString("en-CA", {
                                      year: "numeric",
                                      month: "2-digit",
                                      day: "2-digit",
                                    })
                                    ?.split("/")
                                    .reverse()
                                    .join("-"),
                                );
                                trigger("account.contractStart");
                              }}
                              required={false}
                              error={
                                (errors.account as any)?.contractStart
                                  ?.message as string
                              }
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="account.contractEnd"
                          control={control}
                          rules={{
                            validate: (value) => {
                              const date = new Date(value);
                              const startDate = new Date(
                                getValues("account.contractStart"),
                              );
                              if (startDate && date < startDate)
                                return t("endDateOrder");
                              return true;
                            },
                          }}
                          render={({ field }) => (
                            <BasicDatePicker
                              type="datePicker"
                              {...field}
                              label={t("contractEndDate")}
                              fullWidth
                              onChange={(value: any) => {
                                field.onChange(
                                  new Date(value)
                                    .toLocaleDateString("en-CA", {
                                      year: "numeric",
                                      month: "2-digit",
                                      day: "2-digit",
                                    })
                                    ?.split("/")
                                    .reverse()
                                    .join("-"),
                                );
                                trigger("account.contractEnd");
                              }}
                              required={false}
                              error={
                                (errors.account as any)?.contractEnd
                                  ?.message as string
                              }
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="account.hostingId"
                          control={control}
                          defaultValue={null}
                          rules={{
                            required: watch("account.contractClient")
                              ? t("This field is required.")
                              : false,
                          }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={
                                t("hostingDetails") +
                                (watch("account.contractClient") ? " *" : "")
                              }
                              defaultPlaceholder="phSelectHostingDetails"
                              {...field}
                              options={hostingList}
                              isSearchable
                              isClearable
                              onChange={(selectedOption: any) =>
                                field.onChange(selectedOption)
                              }
                              error={
                                (errors.account as any)?.hostingId
                                  ?.message as string
                              }
                              isLoading={searchLoading === "hostingDetails"}
                              onInputChange={(
                                search: string,
                                { action }: { action: string },
                              ) => {
                                if (
                                  action === "input-change" ||
                                  (action !== "set-value" &&
                                    action !== "menu-close" &&
                                    action !== "input-blur")
                                )
                                  handleDropdownActions(
                                    "hostingDetails",
                                    "searchIt",
                                    search,
                                  );
                                else if (
                                  action === "input-blur" ||
                                  action === "menu-close"
                                ) {
                                  if (dropdownSearchTerm || pageNo > 1)
                                    handleDropdownActions(
                                      "hostingDetails",
                                      "searchIt",
                                      "",
                                    );
                                  else setDropdownSearchTerm("");
                                  setPageNo(1);
                                }
                              }}
                              onMenuScrollToBottom={() => {
                                if (
                                  !(priceListData?.pages[0]?.data as any).last
                                )
                                  handleDropdownActions(
                                    "hostingDetails",
                                    "loadNext",
                                  );
                              }}
                              page={pageNo}
                            />
                          )}
                        />
                      </Grid>
                    </>
                  ) : null}
                </Grid>
                {currentStep === 0 ? (
                  <>
                    <Grid
                      item
                      container
                      xs={12}
                      spacing={2}
                      className="mt-[0px]"
                    >
                      <Accordion
                        defaultExpanded
                        sx={{
                          boxShadow: "none !important",
                          width: "100% !important",
                          padding: "7px 12px 12px 0",
                          borderRadius: "8px !important",
                          borderTop: "4px solid #fff",
                          "&.Mui-expanded": {
                            borderColor: themeColors?.primary,
                          },
                          "& .MuiAccordionSummary-root": {
                            minHeight: "unset !important",
                            paddingRight: 0,
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={
                            <Box
                              sx={{
                                background: "#E1E8E3",
                                borderRadius: "8px",
                                padding: "6px",
                              }}
                            >
                              <ExpandMoreIcon
                                sx={{
                                  stroke: themeColors?.primary,
                                  strokeWidth: "0.3",
                                }}
                              />
                            </Box>
                          }
                          aria-controls="address-content"
                          id="address-header"
                          sx={{
                            "& .MuiAccordionSummary-content": {
                              margin: "0 !important",
                            },
                          }}
                        >
                          <h1
                            className="flex font-Saira font-[600] text-[14px]"
                            style={{ color: themeColors?.primary }}
                          >
                            {!primaryLanguage ? t("primary") : ""}{" "}
                            {t("address")}{" "}
                            {primaryLanguage ? ` (${t(primaryLanguage)})` : ""}
                          </h1>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid item container spacing={2} xs={12}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                {...register("addressesRequest.address")}
                                label={t("address")}
                                placeholder={t("phStreetAddress")}
                                title={t("address")}
                                fullWidth
                                error={
                                  !!(errors.addressesRequest as any)?.address
                                }
                                helperText={
                                  (errors.addressesRequest as any)?.address
                                    ?.message as string
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                {...register("addressesRequest.buildingNumber")}
                                label={t("buildingNumber")}
                                placeholder={t("phBuildingNumber")}
                                title={t("buildingNumber")}
                                fullWidth
                                error={
                                  !!(errors.addressesRequest as any)
                                    ?.buildingNumber
                                }
                                helperText={
                                  (errors.addressesRequest as any)
                                    ?.buildingNumber?.message as string
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                {...register("addressesRequest.street")}
                                label={t("street")}
                                placeholder={t("phAlWaslStreet")}
                                title={t("street")}
                                fullWidth
                                error={
                                  !!(errors.addressesRequest as any)?.street
                                }
                                helperText={
                                  (errors.addressesRequest as any)?.street
                                    ?.message as string
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                {...register("addressesRequest.district")}
                                label={t("district")}
                                placeholder={t("phDistrict")}
                                title={t("district")}
                                fullWidth
                                error={
                                  !!(errors.addressesRequest as any)?.district
                                }
                                helperText={
                                  (errors.addressesRequest as any)?.district
                                    ?.message as string
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                {...register("addressesRequest.city")}
                                label={t("city")}
                                placeholder={t("phCity")}
                                title={t("city")}
                                fullWidth
                                error={!!(errors.addressesRequest as any)?.city}
                                helperText={
                                  (errors.addressesRequest as any)?.city
                                    ?.message as string
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                {...register("addressesRequest.country")}
                                label={t("country")}
                                placeholder={t("phCountryUAE")}
                                title={t("country")}
                                fullWidth
                                error={
                                  !!(errors.addressesRequest as any)?.country
                                }
                                helperText={
                                  (errors.addressesRequest as any)?.country
                                    ?.message as string
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                {...register("addressesRequest.postalCode")}
                                label={t("postalCode")}
                                placeholder={t("phPostalCodeZero")}
                                title={t("postalCode")}
                                fullWidth
                                error={
                                  !!(errors.addressesRequest as any)?.postalCode
                                }
                                helperText={
                                  (errors.addressesRequest as any)?.postalCode
                                    ?.message as string
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                    <Grid
                      item
                      container
                      xs={12}
                      spacing={2}
                      className="mt-[0px]"
                    >
                      <Accordion
                        sx={{
                          boxShadow: "none !important",
                          width: "100% !important",
                          padding: "7px 12px 12px 0",
                          borderRadius: "8px !important",
                          borderTop: "4px solid #fff",
                          "&.Mui-expanded": {
                            borderColor: themeColors?.primary,
                          },
                          "& .MuiAccordionSummary-root": {
                            minHeight: "unset !important",
                            paddingRight: 0,
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={
                            <Box
                              sx={{
                                background: "#E1E8E3",
                                borderRadius: "8px",
                                padding: "6px",
                              }}
                            >
                              <ExpandMoreIcon
                                sx={{
                                  stroke: themeColors?.primary,
                                  strokeWidth: "0.3",
                                }}
                              />
                            </Box>
                          }
                          aria-controls="address-content"
                          id="address-header"
                          sx={{
                            "& .MuiAccordionSummary-content": {
                              margin: "0 !important",
                            },
                          }}
                        >
                          <h1
                            className="flex font-Saira font-[600] text-[14px]"
                            style={{ color: themeColors?.primary }}
                          >
                            {!secondaryLanguage ? t("secondary") : ""}{" "}
                            {t("address")}{" "}
                            {secondaryLanguage
                              ? ` (${t(secondaryLanguage)})`
                              : ""}
                          </h1>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid item container spacing={2} xs={12}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                {...register(
                                  "addressesRequest.secondaryAddress",
                                )}
                                label={t("address")}
                                placeholder={t("phStreetAddress")}
                                title={t("address")}
                                fullWidth
                                error={
                                  !!(errors.addressesRequest as any)
                                    ?.secondaryAddress
                                }
                                helperText={
                                  (errors.addressesRequest as any)
                                    ?.secondaryAddress?.message as string
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                {...register(
                                  "addressesRequest.secondaryBuildingNumber",
                                )}
                                label={t("buildingNumber")}
                                placeholder={t("phBuildingNumber")}
                                title={t("buildingNumber")}
                                fullWidth
                                error={
                                  !!(errors.addressesRequest as any)
                                    ?.secondaryBuildingNumber
                                }
                                helperText={
                                  (errors.addressesRequest as any)
                                    ?.secondaryBuildingNumber?.message as string
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                {...register(
                                  "addressesRequest.secondaryStreet",
                                )}
                                label={t("street")}
                                placeholder={t("phAlWaslStreet")}
                                title={t("street")}
                                fullWidth
                                error={
                                  !!(errors.addressesRequest as any)
                                    ?.secondaryStreet
                                }
                                helperText={
                                  (errors.addressesRequest as any)
                                    ?.secondaryStreet?.message as string
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                {...register(
                                  "addressesRequest.secondaryDistrict",
                                )}
                                label={t("district")}
                                placeholder={t("phDistrict")}
                                title={t("district")}
                                fullWidth
                                error={
                                  !!(errors.addressesRequest as any)
                                    ?.secondaryDistrict
                                }
                                helperText={
                                  (errors.addressesRequest as any)
                                    ?.secondaryDistrict?.message as string
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                {...register("addressesRequest.secondaryCity")}
                                label={t("city")}
                                placeholder={t("phCity")}
                                title={t("city")}
                                fullWidth
                                error={
                                  !!(errors.addressesRequest as any)
                                    ?.secondaryCity
                                }
                                helperText={
                                  (errors.addressesRequest as any)
                                    ?.secondaryCity?.message as string
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                {...register(
                                  "addressesRequest.secondaryCountry",
                                )}
                                label={t("country")}
                                placeholder={t("phCountryUAE")}
                                title={t("country")}
                                fullWidth
                                error={
                                  !!(errors.addressesRequest as any)
                                    ?.secondaryCountry
                                }
                                helperText={
                                  (errors.addressesRequest as any)
                                    ?.secondaryCountry?.message as string
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                {...register(
                                  "addressesRequest.secondaryPostalCode",
                                )}
                                label={t("postalCode")}
                                placeholder={t("phPostalCodeZero")}
                                title={t("postalCode")}
                                fullWidth
                                error={
                                  !!(errors.addressesRequest as any)
                                    ?.secondaryPostalCode
                                }
                                helperText={
                                  (errors.addressesRequest as any)
                                    ?.secondaryPostalCode?.message as string
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                    <Grid item xs={12}>
                      <h1
                        className="flex font-Saira font-[700] text-[17px]"
                        style={{ color: themeColors?.primary }}
                      >
                        {t("userDetails")}
                      </h1>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        {...register("name", {
                          validate: {
                            required: (value) =>
                              value?.trim() !== "" ||
                              t("This field is required."),
                          },
                        })}
                        label={`${t("user")} ${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                        placeholder={localizedContactPersonPlaceholder(
                          primaryLanguage,
                        )}
                        title={`${t("user")} ${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                          .replace(/\s+/g, " ")
                          .trim()}
                        fullWidth
                        error={!!errors.name}
                        helperText={errors.name?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        {...register("secondaryName")}
                        label={`${t("user")} ${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                        placeholder={localizedContactPersonPlaceholder(
                          secondaryLanguage,
                        )}
                        title={`${t("user")} ${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                          .replace(/\s+/g, " ")
                          .trim()}
                        fullWidth
                        error={!!errors.secondaryName}
                        helperText={errors.secondaryName?.message as string}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type="text"
                        {...register("phone", {
                          validate: {
                            phoneLength: (value) =>
                              value === "" ||
                              value === null ||
                              (value?.length >= 5 && value?.length <= 17) ||
                              t("phoneLength"),
                          },
                        })}
                        label={t("phoneNumber")}
                        placeholder={t("phContactPhone")}
                        title={t("phoneNumber")}
                        fullWidth
                        error={!!errors.phone}
                        helperText={errors.phone?.message as string}
                        InputLabelProps={{ shrink: true }}
                        onChange={(
                          e: ChangeEvent<
                            HTMLInputElement | HTMLTextAreaElement
                          >,
                        ) => {
                          const cleaned = e.target.value.replace(
                            /[^0-9+]/g,
                            "",
                          );
                          setValue("phone", cleaned);
                          if (!IsBtnEnabled) setIsBtnEnabled(true);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        {...register("address")}
                        label={t("address")}
                        placeholder={t("phContactAddress")}
                        title={t("address")}
                        fullWidth
                        error={!!errors.address}
                        helperText={errors.address?.message as string}
                        InputLabelProps={{ shrink: true }}
                        multiline
                        rows={1}
                        sx={{
                          "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                            width: "100%",
                            paddingRight: "0px",
                            paddingBottom: 0,
                          },
                          "& .MuiInputBase-inputMultiline": {
                            resize: "vertical",
                            minHeight: "29px",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="source"
                        control={control}
                        defaultValue={null}
                        rules={{ required: false }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={t("source")}
                            defaultPlaceholder="phSelectSource"
                            {...field}
                            options={sourceList}
                            isSearchable
                            isClearable
                            onChange={(selectedOption: any) =>
                              field.onChange(selectedOption)
                            }
                            error={errors?.source?.message as string}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3} className="mt-[-4px]">
                      <Controller
                        name="loginAccess"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <div>
                            <CommonCheckbox
                              {...field}
                              label={t("loginAccess")}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                field.onChange(event.target.checked);
                                if (!event.target.checked) {
                                  if (!params?.id) {
                                    setValue("username", "");
                                  }
                                  setValue("password", "");
                                  setValue("confirmPassword", "");
                                }
                              }}
                              checked={field.value}
                              color={themeColors?.primary}
                            />
                          </div>
                        )}
                      />
                    </Grid>
                    {watch("loginAccess") ? (
                      <>
                        <Grid item xs={12} md={3}>
                          <TextField
                            type="email"
                            {...register("username", {
                              required: watch("loginAccess")
                                ? t("This field is required.")
                                : false,
                              pattern: {
                                value:
                                  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                message: t("validEmail"),
                              },
                            })}
                            label={
                              t("email") + (watch("loginAccess") ? " *" : "")
                            }
                            placeholder={t("phUserEmail")}
                            title={
                              t("email") + (watch("loginAccess") ? " *" : "")
                            }
                            fullWidth
                            error={!!errors.username}
                            helperText={errors.username?.message as string}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        {params?.id ? null : (
                          <>
                            <Grid item xs={12} md={3}>
                              <TextField
                                type={
                                  watch("showPassword") ? "text" : "password"
                                }
                                {...register("password", {
                                  required: watch("loginAccess")
                                    ? t("This field is required.")
                                    : false,
                                  validate:
                                    watch("loginAccess") ||
                                    watch("password")?.length
                                      ? {
                                          passwordLength: (value) =>
                                            value?.length >= 8 ||
                                            t("minPasswordLength"),
                                        }
                                      : undefined,
                                })}
                                label={
                                  t("password") +
                                  (watch("loginAccess") ? " *" : "")
                                }
                                placeholder={t("phEnterPassword")}
                                title={
                                  t("password") +
                                  (watch("loginAccess") ? " *" : "")
                                }
                                fullWidth
                                error={!!errors.password}
                                helperText={errors.password?.message as string}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                  endAdornment: watch("password") ? (
                                    <InputAdornment
                                      position="end"
                                      className="cursor-pointer"
                                    >
                                      {watch("showPassword") ? (
                                        <VisibilityIcon
                                          style={{
                                            color: themeColors?.primary,
                                          }}
                                          onClick={() =>
                                            setValue("showPassword", false)
                                          }
                                        />
                                      ) : (
                                        <VisibilityOffIcon
                                          style={{
                                            color: themeColors?.primary,
                                          }}
                                          onClick={() =>
                                            setValue("showPassword", true)
                                          }
                                        />
                                      )}
                                    </InputAdornment>
                                  ) : null,
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                type={
                                  watch("showConfirmPassword")
                                    ? "text"
                                    : "password"
                                }
                                {...register("confirmPassword", {
                                  required: watch("loginAccess")
                                    ? t("This field is required.")
                                    : false,
                                  validate:
                                    watch("loginAccess") ||
                                    watch("password")?.length ||
                                    watch("confirmPassword")?.length
                                      ? {
                                          passwordMatch: (value) =>
                                            value === watch("password") ||
                                            t("confirmPasswordNewPassword"),
                                        }
                                      : undefined,
                                })}
                                label={
                                  t("confirmNewPassword") +
                                  (watch("loginAccess") ? " *" : "")
                                }
                                placeholder={t("phReenterPassword")}
                                title={
                                  t("confirmNewPassword") +
                                  (watch("loginAccess") ? " *" : "")
                                }
                                fullWidth
                                error={!!errors.confirmPassword}
                                helperText={
                                  errors.confirmPassword?.message as string
                                }
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                  endAdornment: watch("confirmPassword") ? (
                                    <InputAdornment
                                      position="end"
                                      className="cursor-pointer"
                                    >
                                      {watch("showConfirmPassword") ? (
                                        <VisibilityIcon
                                          style={{
                                            color: themeColors?.primary,
                                          }}
                                          onClick={() =>
                                            setValue(
                                              "showConfirmPassword",
                                              false,
                                            )
                                          }
                                        />
                                      ) : (
                                        <VisibilityOffIcon
                                          style={{
                                            color: themeColors?.primary,
                                          }}
                                          onClick={() =>
                                            setValue(
                                              "showConfirmPassword",
                                              true,
                                            )
                                          }
                                        />
                                      )}
                                    </InputAdornment>
                                  ) : null,
                                }}
                              />
                            </Grid>
                          </>
                        )}
                      </>
                    ) : null}
                  </>
                ) : null}
                {currentStep > 0 ? (
                  <Grid item xs={12} className="text-start">
                    <Button
                      variant="contained"
                      className={`${isScreenMediumOrGreater ? "" : "absolute"} font-Saira font-semibold leading-[26px] bg-white text-black ${isScreenMediumOrGreater ? "" : "mt-[60px]"} px-[20px]`}
                      onClick={() => setCurrentStep(0)}
                    >
                      {t("Previous")}
                    </Button>
                  </Grid>
                ) : null}
                {updateModal && (
                  <FormDialog
                    open={updateModal}
                    onClose={(event: any, reason: any) => {
                      if (event && reason !== "backdropClick") {
                        setUpdateModal(false);
                        updateClient({ ...client, updateImpact: false });
                      }
                    }}
                    hideClose={true}
                    title={t("applyChangesToJCWO")}
                    content={<Typography>{t(`wantToApplyChanges`)}</Typography>}
                    saveFunction={() => {
                      setUpdateModal(false);
                      updateClient({ ...client, updateImpact: true });
                    }}
                    submitButtonText="yes"
                    cancelBtnText="no"
                  />
                )}
              </Grid>
            }
          />
        )}
      </div>
    </>
  );
}
